import type {ApolloCache} from '@apollo/client';

import {SETTINGS} from 'client/Assignments/constants';

import type {AssignmentStatusCounts} from './hooks/useAssignmentStatusCounts';
import type {TeacherAssignmentRowFieldsFragment} from './queries/TeacherAssignments.queries.generated';

interface Args {
  assignmentCount: number;
  hasFiltersOn: boolean;
  /** The tab's own list query — the scope question only matters once it resolves empty. */
  isListLoading: boolean;
  counts?: AssignmentStatusCounts;
  isCountsLoading: boolean;
  hasCountsError: boolean;
}

interface AssignmentsScopeState {
  /** The scope-wide check is still resolving — show the list spinner, not a premature empty state. */
  isCheckingScope: boolean;
  /** The whole scope has no assignments at all — render the onboarding state. */
  isScopeEmpty: boolean;
}

/**
 * The design has a "no assignments yet" empty state for the whole scope — "scope" meaning all classes
 * (the cross-class view) or one class. To show it correctly we need to know whether the scope has zero
 * assignments across EVERY status, not just the active open/closed/... tab. The status selector's
 * totals already answer that: the four statuses partition every assignment (the status calc has a
 * closed catch-all), so their sum is the scope total — no separate existence query needed. This is
 * only consulted with filters off, where the counts are scope-pure (they otherwise follow filters).
 */
export const getAssignmentsScope = ({
  assignmentCount,
  hasFiltersOn,
  isListLoading,
  counts,
  isCountsLoading,
  hasCountsError
}: Args): AssignmentsScopeState => {
  // Only relevant when nothing is showing and no filter is narrowing the view.
  const isUndetermined = !hasFiltersOn && !isListLoading && assignmentCount === 0;

  // While a filter change refetches, `counts` still holds the previous variables' totals —
  // isCountsLoading gates that staleness out of the scope decision.
  const scopeTotal =
    !isCountsLoading && counts
      ? counts.open + counts.closed + counts.scheduled + counts.draft
      : undefined;

  return {
    isCheckingScope: isUndetermined && !hasCountsError && scopeTotal === undefined,
    // A failed count fetch must not masquerade as an empty scope (a false onboarding state); the
    // list's own hasError drives the retry panel instead.
    isScopeEmpty: isUndetermined && !hasCountsError && scopeTotal === 0
  };
};

/** A classroom-assignment row as both release entry points select it. */
export interface ReleaseScopeRow {
  classroomId: string;
  gradesReleased?: boolean | null;
  gradesReleasedAt?: string | null;
}

/**
 * Whether every class in scope has already had its grades released, which is the one condition that
 * leaves a release with nothing to do. Shared by the assignments list and the submission toolbar so
 * the two entry points into the release modal enable and disable together.
 *
 * Scoped to the open class, or to every class the teacher teaches when there is none — which is what
 * a release from there spans, so a partly-released assignment is not released. An empty list means
 * "we can't see any classes" rather than "all released": that is what a creator who teaches none of
 * them, or an admin masquerading, actually gets back.
 */
export const isReleasedForScope = (
  rows: readonly ReleaseScopeRow[] | null | undefined,
  classroomId?: string
): boolean => {
  const visible = rows ?? [];
  const scoped = classroomId ? visible.filter((row) => row.classroomId === classroomId) : visible;
  return scoped.length > 0 && scoped.every((row) => row.gradesReleased === true);
};

/**
 * Most recent release across the classes in scope, or `null` if none have released.
 *
 * Reads the timestamps rather than `gradesReleased`, which is also true for a class with nothing
 * to gate.
 */
export const releasedAtForScope = (
  rows: readonly ReleaseScopeRow[] | null | undefined,
  classroomId?: string
): string | null => {
  const visible = rows ?? [];
  const scoped = classroomId ? visible.filter((row) => row.classroomId === classroomId) : visible;
  const timestamps = scoped
    .map((row) => row.gradesReleasedAt)
    .filter((at): at is string => Boolean(at));
  return timestamps.length
    ? timestamps.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b))
    : null;
};

/** A classroom-assignment row carrying its assignment's FRQ answer. */
export interface FrqScopeRow {
  containsManuallyGradedQuestions?: boolean | null;
}

/**
 * Whether the assignment has any manually-graded questions. Asked of the classroom assignments
 * because `Assignment` itself does not expose the field; every classroom assignment of a given
 * assignment answers identically, so one `true` settles it.
 *
 * Not scoped to a class, unlike {@link isReleasedForScope} — the questions on an assignment are the
 * same whichever class is in view. An actor who can see none of its classes reads false here, which
 * is why callers treat submissions as their own proof of FRQs rather than relying on this alone.
 */
export const hasManuallyGradedQuestions = (
  rows: readonly FrqScopeRow[] | null | undefined
): boolean => (rows ?? []).some((row) => row.containsManuallyGradedQuestions === true);

/**
 * Why a release is unavailable when nothing has been graded to completion — nobody has submitted, or
 * no submission has a finished grade. Shared verbatim by the assignments list's menu item and the
 * submission toolbar's button so the two entry points explain themselves identically.
 */
export const NO_RELEASABLE_SUBMISSIONS = 'There are no submissions ready to be released';

/** How many students a release would and would not reach, for the release modal's summary. */
export interface ReleaseStudentCounts {
  /** Submitted, but their grade is unfinished — a release now leaves them awaiting grading. */
  awaitingGrade: number;
  /** Never turned the assignment in, so there is nothing to grade or release. */
  notSubmitted: number;
  /** Students in scope, submitted or not. */
  total: number;
}

/** An assignment as every release surface selects it, via `AssignmentGradingCounts`. */
export interface ReleaseStudentCountsRow {
  countOfCompletedStudents?: number | null;
  countOfIncompleteStudents?: number | null;
  countOfStudentsAwaitingGrade?: number | null;
}

/**
 * The release modal's student-level counts, for whichever classes the selecting query scoped them
 * to. Zeroed when the counts weren't requested, i.e. with the grading flag off, where no release
 * surface is mounted to read them.
 */
export const getReleaseStudentCounts = (
  assignment: ReleaseStudentCountsRow | null | undefined
): ReleaseStudentCounts => ({
  awaitingGrade: assignment?.countOfStudentsAwaitingGrade ?? 0,
  notSubmitted: assignment?.countOfIncompleteStudents ?? 0,
  total: (assignment?.countOfCompletedStudents ?? 0) + (assignment?.countOfIncompleteStudents ?? 0)
});

/**
 * The `AssignmentGradingCounts` fields a grade save can move: the guess leaves `ungraded` for
 * `grading_in_progress` on the first save, and joins the releasable count once its grade is
 * complete — which is also when its student stops awaiting one.
 * `countOfSubmittedManuallyGradedGuesses` spans all three statuses, so it never moves, and the
 * submitted/unsubmitted split moves on submission rather than on grading.
 */
const GRADING_COUNT_FIELDS = [
  'countOfUngradedGuesses',
  'countOfGradingInProgressGuesses',
  'countOfReleasableGradedGuesses',
  'countOfStudentsAwaitingGrade'
] as const;

/**
 * Drops an assignment's cached grading counts so every surface reading them — the assignments
 * list, the release modal, the submission toolbar's release action — re-reads them from the
 * server. Grading writes land on the `Guess`, so nothing normalizes them onto these aggregates.
 *
 * Evicting by field name clears every `classroomIds` variant at once, since Apollo stores each
 * argument set under its own key.
 */
export const evictAssignmentGradingCounts = (
  cache: ApolloCache<unknown>,
  assignmentId: string
): void => {
  const id = cache.identify({__typename: 'Assignment', id: assignmentId});
  if (!id) {
    return;
  }
  GRADING_COUNT_FIELDS.forEach((fieldName) => cache.evict({id, fieldName}));
};

/** A classroom-assignment row carrying the settings that decide when a student sees results. */
export interface ResultsVisibilityRow {
  classroomId: string;
  settings?: {correctAnswerSetting?: string | null; dueDate?: string | null} | null;
}

/**
 * Whether releasing now would still leave students with nothing to look at: `showAfterDueDate`
 * withholds results until the due date, so a release before then shows them no score at all.
 *
 * True when *any* class in scope is in that state. A mixed scope is the case most likely to
 * mislead, so it warns rather than staying quiet — over-warning costs a sentence, the alternative
 * is the unconditional promise this replaces.
 *
 * Resolved per classroom, which is as far as a teacher can see: `StudentAssignment` coalesces a
 * per-student override over the classroom value, but there is no educator-scoped read that lists
 * student assignments, so an override on a single student is missed here.
 */
export const hidesResultsUntilDueDate = (
  rows: readonly ResultsVisibilityRow[] | null | undefined,
  classroomId?: string,
  now: number = Date.now()
): boolean => {
  const scoped = classroomId
    ? (rows ?? []).filter((row) => row.classroomId === classroomId)
    : (rows ?? []);
  return scoped.some(({settings}) => {
    if (settings?.correctAnswerSetting !== SETTINGS.SHOW_AFTER_DUE_DATE) {
      return false;
    }
    // No due date on a due-date setting can never elapse, so the grade stays hidden either way.
    const dueDate = settings?.dueDate ? Date.parse(settings.dueDate) : NaN;
    return Number.isNaN(dueDate) || dueDate > now;
  });
};

/** How far a row's FRQ grading has got. */
export type FrqGradingStatus = 'needsGrading' | 'readyToRelease' | 'released';

interface GradingStatusArgs {
  /** Every submitted FRQ response in scope — the denominator. */
  submittedCount: number;
  /** Finished grades a student can already see. */
  publishedCount: number;
  /** Finished grades no student can see yet, because no class of theirs is released. */
  unreleasedCompletedCount: number;
  isReleased: boolean;
}

/**
 * Publishing a grade requires releasing its class, so the two counts are disjoint and together are
 * the finished work: a published grade always has a released class, which is what excludes it from
 * `unreleasedCompletedCount`.
 *
 * @returns `null` when there is nothing to report — no submissions, or every grade already out.
 */
export const getFrqGradingStatus = ({
  submittedCount,
  publishedCount,
  unreleasedCompletedCount,
  isReleased
}: GradingStatusArgs): FrqGradingStatus | null => {
  if (submittedCount === 0) {
    return null;
  }
  if (isReleased) {
    return 'released';
  }
  if (publishedCount + unreleasedCompletedCount < submittedCount) {
    return 'needsGrading';
  }
  return unreleasedCompletedCount > 0 ? 'readyToRelease' : null;
};

interface GradeAffordanceArgs {
  assignment: TeacherAssignmentRowFieldsFragment;
  /** This row is a draft — nothing to grade. Per row, since the All tab mixes statuses. */
  isDraft: boolean;
  isFrqGradingEnabled: boolean;
  isFrqFlagLoading: boolean;
  /** Absent on All Classwork, where the row spans every class the teacher teaches. */
  classroomId?: string;
}

/**
 * What the row's grading affordance should offer, or `null` for a row that has no grading to
 * offer at all: a draft, the flag off, or an assignment with no manually-graded questions.
 *
 * An assignment whose FRQs nobody has turned in yet still returns an affordance, with
 * `hasSubmissions: false` — the button stays on the row, disabled, rather than disappearing until
 * the first submission lands. The counts alone cannot tell that row from an MCQ-only one, which is
 * why {@link hasManuallyGradedQuestions} is consulted for the rows they read alike.
 */
export const getGradeAffordance = ({
  assignment,
  isDraft,
  isFrqGradingEnabled,
  isFrqFlagLoading,
  classroomId
}: GradeAffordanceArgs): {
  publishedCount: number;
  submittedCount: number;
  unreleasedCompletedCount: number;
  studentCounts: ReleaseStudentCounts;
  isReleased: boolean;
  releasedAt: string | null;
  gradingStatus: FrqGradingStatus | null;
  hasSubmissions: boolean;
  resultsHiddenUntilDueDate: boolean;
} | null => {
  if (isDraft || !isFrqGradingEnabled || isFrqFlagLoading) {
    return null;
  }
  // Undefined when the counts weren't requested, i.e. with the grading flag off.
  const submittedCount = assignment.countOfSubmittedManuallyGradedGuesses ?? 0;
  // Submissions imply FRQs, and they answer where the marker cannot: an actor who teaches none of
  // the assignment's classes sees no classroom assignments but can still have counts.
  if (submittedCount === 0 && !hasManuallyGradedQuestions(assignment.classroomAssignments)) {
    return null;
  }
  // Every submitted response holds exactly one status, so published is what the other two leave.
  const outstanding =
    (assignment.countOfUngradedGuesses ?? 0) + (assignment.countOfGradingInProgressGuesses ?? 0);
  const publishedCount = Math.max(submittedCount - outstanding, 0);
  const unreleasedCompletedCount = assignment.countOfReleasableGradedGuesses ?? 0;
  const isReleased = isReleasedForScope(assignment.classroomAssignments, classroomId);

  return {
    publishedCount,
    submittedCount,
    unreleasedCompletedCount,
    studentCounts: getReleaseStudentCounts(assignment),
    isReleased,
    releasedAt: releasedAtForScope(assignment.classroomAssignments, classroomId),
    gradingStatus: getFrqGradingStatus({
      submittedCount,
      publishedCount,
      unreleasedCompletedCount,
      isReleased
    }),
    hasSubmissions: submittedCount > 0,
    resultsHiddenUntilDueDate: hidesResultsUntilDueDate(
      assignment.classroomAssignments,
      classroomId
    )
  };
};
